import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  getPerformerAndVenueForEvent,
  lookupEventInCatalog,
} from 'src/utils/eventWithDataUtils';
import {
  CatalogClient,
  EventGroupClient,
  EventGroupMemberEvent,
  EventWithData,
  Feature,
  Performer,
  Venue,
} from 'src/WebApiController';

export const PLACEHOLDER_EVENT_GROUP_QUERY_KEY =
  'EventGroupClient.getEventGroupEvents';

export type EventGroupSlot = {
  /** Lower-cased so it matches the ids the CatalogDataContext keys events by. */
  posEventId: string;
  sellerEventId: string;
  allocated: boolean;
  name: string | null;
  venueId: number | null;
  venueConfigId: number | null;
  categoryId: number | null;
  startDate: string | null;
  endDate: string | null;
  eventDateTime: string | null;
  viagogoEventId: number | null;
  eventMappingId: string | null;
  event?: EventWithData;
  performer?: Performer | null;
  venue?: Venue | null;
};

/**
 * virtId used to address a slot's event in the catalog: an allocated slot is
 * addressed by its viagogoEventId; an unallocated placeholder falls back to its
 * POS event id (which is the placeholder event's virtId server-side).
 */
const slotVirtId = (slot: EventGroupMemberEvent): string =>
  slot.viagogoEventId != null
    ? String(slot.viagogoEventId)
    : (slot.eventMappingId ?? slot.posEventId);

/**
 * Fetches all member events (placeholder slots + already-allocated events) of a placeholder
 * event group, plus the group-level metadata. Gated behind Feature.PlaceholderEventGroup.
 */
export function useGetEventGroupEvents(groupId: string | null) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasPlaceholderEventGroupFeature = useUserHasFeature(
    Feature.PlaceholderEventGroup
  );

  const shouldQuery =
    hasPlaceholderEventGroupFeature &&
    !!groupId &&
    !!activeAccountWebClientConfig?.activeAccountId;

  const query = useQuery({
    queryKey: [
      PLACEHOLDER_EVENT_GROUP_QUERY_KEY,
      activeAccountWebClientConfig?.activeAccountId,
      groupId,
    ],
    async queryFn() {
      if (!shouldQuery) return null;

      try {
        return await new EventGroupClient(
          activeAccountWebClientConfig
        ).getEventGroupEvents({ eventGroupId: groupId! });
      } catch (err: unknown) {
        trackError('EventGroupClient.getEventGroupEvents', err as ErrorTypes, {
          groupId,
        });
        throw err;
      }
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
  });

  // Fetch by both id spaces so the catalog resolves allocated events (by
  // viagogoEventId) and placeholders (by posEventId), and returns the
  // posId→viagId lookup used below.
  const virtualIds = useMemo(() => {
    const ids = new Set<string>();
    for (const { viagogoEventId, eventMappingId } of query.data?.members ??
      []) {
      if (viagogoEventId != null) {
        ids.add(String(viagogoEventId));
      }
      if (eventMappingId != null) {
        ids.add(eventMappingId);
      }
    }
    return [...ids];
  }, [query.data?.members]);

  const catalogQuery = useQuery({
    queryKey: ['getCatalogFull', 'eventGroup', groupId, virtualIds],
    enabled: virtualIds.length > 0,
    async queryFn() {
      try {
        return await new CatalogClient(
          activeAccountWebClientConfig
        ).getCatalogFull(virtualIds);
      } catch (err: unknown) {
        trackError('CatalogClient.getCatalogFull', err as ErrorTypes, {
          groupId,
        });
        throw err;
      }
    },
    refetchOnWindowFocus: false,
  });

  const slots = useMemo<EventGroupSlot[]>(() => {
    return (query.data?.members ?? []).map((slot) => {
      const eventWithData = lookupEventInCatalog(
        catalogQuery.data,
        slotVirtId(slot),
        slot.posEventId
      );
      const { performer, venue } = getPerformerAndVenueForEvent(
        eventWithData?.event,
        catalogQuery.data
      );
      return {
        ...slot,
        allocated: slot.viagogoEventId != null,
        event: eventWithData ?? undefined,
        performer: performer,
        venue: venue,
      };
    });
  }, [catalogQuery.data, query.data?.members]);

  return {
    slots,
    name: query.data?.name ?? null,
    isLoading: shouldQuery && query.isLoading,
    refetch: query.refetch,
  };
}
